import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, TextField,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  InputAdornment, LinearProgress,
} from '@mui/material';
import { Add, Refresh, AttachMoney, TrendingUp, AccountBalance } from '@mui/icons-material';
import { projectService, ProjectExpense, Project } from '../../services/projectService';
import { formatCurrency } from '../../utils/currency';

const CostTrackingPage: React.FC = () => {
  const [expenses, setExpenses] = useState<ProjectExpense[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [totalExpenses, setTotalExpenses] = useState('0');
  const [formData, setFormData] = useState({
    projectId: '', expenseDate: '', expenseType: '', amount: '', description: '',
  });
  const [formLoading, setFormLoading] = useState(false);

  const expenseTypes = ['LABOR', 'MATERIALS', 'EQUIPMENT', 'TRAVEL', 'SUBCONTRACTOR', 'OVERHEAD', 'OTHER'];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);

      if (selectedProject) {
        const expenseData = await projectService.getProjectExpenses(selectedProject);
        setExpenses(expenseData.expenses);
        setTotalExpenses(expenseData.totalExpensesFormatted);
      } else {
        setExpenses([]);
        setTotalExpenses('₹0');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenDialog = () => {
    setFormData({
      projectId: selectedProject ? selectedProject.toString() : '',
      expenseDate: new Date().toISOString().split('T')[0],
      expenseType: '', amount: '', description: '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.projectId || !formData.expenseType || !formData.amount || !formData.expenseDate) {
      setError('Project, date, type, and amount are required');
      return;
    }
    setFormLoading(true);
    try {
      await projectService.createExpense(parseInt(formData.projectId), {
        expenseDate: formData.expenseDate,
        expenseType: formData.expenseType,
        amount: parseFloat(formData.amount),
        description: formData.description || undefined,
      });
      setDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save expense');
    } finally {
      setFormLoading(false);
    }
  };

  const selectedProjectData = projects.find(p => p.id === selectedProject);
  const estBudget = Number(selectedProjectData?.estimated_budget) || 0;
  const actCost = Number(selectedProjectData?.actual_cost) || 0;
  const budgetUtilization = estBudget > 0 ? (actCost / estBudget * 100) : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>Cost Tracking</Typography>
          <Typography variant="body2" color="text.secondary">Track project expenses and budget utilization</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={fetchData}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog} disabled={!selectedProject}>
            Add Expense
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField select fullWidth label="Select Project" value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value === '' ? '' : parseInt(e.target.value as string))}>
          <MenuItem value="">-- Select a project to view costs --</MenuItem>
          {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.project_name} ({p.project_code})</MenuItem>)}
        </TextField>
      </Paper>

      {selectedProject && selectedProjectData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
              <AccountBalance color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">{formatCurrency(selectedProjectData.estimated_budget)}</Typography>
              <Typography variant="caption" color="text.secondary">Budget</Typography>
            </CardContent></Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
              <AttachMoney color="warning" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">{formatCurrency(selectedProjectData.actual_cost)}</Typography>
              <Typography variant="caption" color="text.secondary">Actual Cost</Typography>
            </CardContent></Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
              <TrendingUp color={budgetUtilization > 100 ? 'error' : 'success'} sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">{budgetUtilization.toFixed(1)}%</Typography>
              <Typography variant="caption" color="text.secondary">Budget Used</Typography>
            </CardContent></Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" fontWeight="bold" color={selectedProjectData.estimated_budget - selectedProjectData.actual_cost >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(selectedProjectData.estimated_budget - selectedProjectData.actual_cost)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Remaining</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {selectedProject && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>Budget Utilization</Typography>
          <LinearProgress variant="determinate" value={Math.min(budgetUtilization, 100)}
            color={budgetUtilization > 100 ? 'error' : budgetUtilization > 80 ? 'warning' : 'primary'}
            sx={{ height: 10, borderRadius: 5 }} />
        </Box>
      )}

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : selectedProject ? (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No expenses recorded</Typography>
                  </TableCell></TableRow>
                ) : expenses.map(expense => (
                  <TableRow key={expense.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell>{new Date(expense.expense_date).toLocaleDateString()}</TableCell>
                    <TableCell><Chip label={expense.expense_type} size="small" /></TableCell>
                    <TableCell>{expense.description || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(expense.amount)}</TableCell>
                  </TableRow>
                ))}
                {expenses.length > 0 && (
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalExpenses}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Select a project to view cost tracking</Typography>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Expense</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label="Project" value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })} required>
                {projects.map(p => <MenuItem key={p.id} value={p.id.toString()}>{p.project_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Date" type="date" value={formData.expenseDate}
                onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField select fullWidth label="Type" value={formData.expenseType}
                onChange={(e) => setFormData({ ...formData, expenseType: e.target.value })} required>
                {expenseTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Amount" type="number" value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={formLoading}>{formLoading ? 'Saving...' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CostTrackingPage;
